/**
 * Thin fetch wrapper for API calls.
 *
 * Injects `Authorization: Bearer <access_token>` from the currently active
 * AuthProvider session. The token is read from a tiny module-level holder
 * updated by `AuthTokenSync` (mounted at app root) — keeps this helper
 * synchronous and free of React imports so it can be called from anywhere.
 *
 * On 401: redirects the browser to `/login?next=<current path>` so the user
 * can re-authenticate. Token refresh is handled by oidc-client-ts's silent
 * renew (configured in AuthProvider).
 */
import { getConfig } from '../config/env';

export interface Envelope<T> {
  data: T;
  meta?: Record<string, unknown>;
}

export interface FetchOptions extends RequestInit {
  /** Override the cached access token (rare — mostly for tests). */
  authToken?: string;
}

let cachedAccessToken: string | null = null;

/** Set by `AuthTokenSync`; called whenever the OIDC user changes. */
export function setAuthToken(token: string | null): void {
  cachedAccessToken = token;
}

export function getAuthToken(): string | null {
  return cachedAccessToken;
}

export async function apiFetch<T>(path: string, options: FetchOptions = {}): Promise<T> {
  const cfg = getConfig();
  const base = cfg.API_BASE_URL.replace(/\/$/, '');
  const url = path.startsWith('http') ? path : `${base}${path.startsWith('/') ? '' : '/'}${path}`;

  const headers = new Headers(options.headers);
  const token = options.authToken ?? cachedAccessToken;
  if (token) {
    headers.set('Authorization', `Bearer ${token}`);
  }
  if (!headers.has('Accept')) headers.set('Accept', 'application/json');

  const res = await fetch(url, { ...options, headers });

  if (res.status === 401) {
    handleUnauthorized();
    throw new Error(`Unauthorized: ${url}`);
  }

  if (!res.ok) {
    throw new Error(`Request failed: ${res.status} ${res.statusText} — ${url}`);
  }

  const raw = (await res.json()) as unknown;
  return unwrap<T>(raw);
}

/**
 * In fake-mode we don't actually have a real auth flow, so just throw and let
 * the call site surface the error. In cognito-mode we redirect to /login
 * preserving the current path so the user lands back here after sign-in.
 */
function handleUnauthorized(): void {
  if (typeof window === 'undefined') return;
  const cfg = getConfig();
  if (cfg.AUTH_MODE === 'fake') return;
  // Avoid redirect loops if we're already on a public route.
  if (
    window.location.pathname === '/login' ||
    window.location.pathname.startsWith('/auth/')
  ) {
    return;
  }
  // Clear any cached Cognito session — the token we sent was rejected, so
  // a forced re-login is required. Without this, the AuthProvider restores
  // the same invalid session from localStorage on the next page load and
  // we 401 again.
  try {
    Object.keys(window.localStorage)
      .filter((k) => k.startsWith('CognitoIdentityServiceProvider.'))
      .forEach((k) => window.localStorage.removeItem(k));
  } catch {
    // Ignore — incognito mode or storage quota.
  }
  setAuthToken(null);
  const next = encodeURIComponent(window.location.pathname + window.location.search);
  window.location.assign(`/login?next=${next}`);
}

/** Unwrap `{ data, meta }` envelopes if present, else return raw. */
export function unwrap<T>(raw: unknown): T {
  if (raw && typeof raw === 'object' && 'data' in (raw as Record<string, unknown>)) {
    return (raw as Envelope<T>).data;
  }
  return raw as T;
}

/**
 * Validate a response against a Zod schema (or any `.parse` shaped validator).
 * In mock mode this is a no-op; real wiring will swap in the live validators.
 */
export function validate<T>(value: T, validator?: { parse: (v: unknown) => T }): T {
  if (!validator) return value;
  return validator.parse(value);
}
